import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ExcelRow,
  ExcelStyle,
  GridApi,
  ValueFormatterParams,
} from '@ag-grid-community/core';

import {
  CompareBudgetVersion,
  CompareGridData,
} from '@widgets/compare-budget/compare-budget.component';

import {
  AgBeCheckboxGroupRendererComponent,
  ICheckboxCellRendererParams,
} from '@features/ag-be-checkbox-group-renderer/ag-be-checkbox-group-renderer.component';
import {
  AgBudgetAttributeComponentParams,
  AgBudgetGroupHeaderComponent,
} from '@features/ag-budget-group-header/ag-budget-group-header.component';
import {
  attributeColumnDef,
  getAttributeColumns,
} from '@features/budget-attributes/services/budget-attributes.service';

import { AgPulseMinusComponent } from '@shared/ag-components/ag-pulse-minus/ag-pulse-minus.component';
import { TableConstants } from '@shared/constants/table.constants';
import { Currency } from '@shared/services/gql.service';
import { TrialModel } from '@shared/store/trials/trials.store';
import { AuxExcelFormats, AuxExcelStyleKeys, AuxExcelStyles } from '@shared/utils';
import { Maybe, Utils } from '@shared/utils/utils';

const ChangeOrderAttributesLSKey = 'co_compare_activities_header';

type CompareBudgetColumnContext = {
  budgetData: CompareGridData[];
  currency: Currency;
  organizationName: string;
  rowSelection: {
    disabled: boolean;
    disabledTooltip: string;
  };
  toBudgetVersion: Maybe<CompareBudgetVersion>;
};

export const getCompareBudgetColumnDefs = (ctx: CompareBudgetColumnContext) => {
  const attributesColumnGroupDef = getAttributesColumnGroupDef(ctx);

  return {
    attributesColumnGroupDef,
    autoGroupColumnDef: getAutoGroupColumnDef(ctx, attributesColumnGroupDef),
    currentBudgetColumnGroupDef: getCurrentBudgetColumnGroupDef(ctx),
    getExcelStyles,
    hiddenColumns: getHiddenColumns(ctx),
    toBudgetColumnGroupDef: getToBudgetColumnGroupDef(ctx),
    varianceColumnGroupDef: getVarianceColumnGroupDef(ctx),
  };
};

const unitColumn = {
  cellClass: ['ag-cell-align-right', 'budget-unit'],
  headerClass: 'ag-header-align-center',
  minWidth: 80,
  resizable: true,
  valueFormatter: Utils.unitFormatter,
  width: 150,
};

const unitCost = (ctx: CompareBudgetColumnContext, addBorder = false) => ({
  cellClass: (params: CellClassParams) => {
    const defaultClasses = ['ag-cell-align-right'];

    if (addBorder) {
      defaultClasses.push(AuxExcelStyleKeys.BORDER_RIGHT);
    }

    if (params?.node?.data?.contract_direct_cost_currency) {
      return [`budgetCost${params?.node?.data?.contract_direct_cost_currency}`, ...defaultClasses];
    }

    return ['budgetCostNoSymbol', ...defaultClasses];
  },
  headerClass: 'ag-header-align-center',
  minWidth: 120,
  resizable: true,
  valueFormatter: (params: ValueFormatterParams) => {
    if (params.value) {
      return Utils.currencyFormatter(Number(params.value), {}, ctx.currency);
    } else {
      return Utils.zeroHyphen;
    }
  },
  width: 150,
});

const getHiddenColumns = (ctx: CompareBudgetColumnContext): (ColDef | ColGroupDef)[] => [
  {
    children: [
      { field: 'cost_category', headerName: 'Cost Category', hide: true, rowGroup: true },
      { field: 'group0', headerName: 'Category', hide: true, rowGroup: true },
    ],
    colId: 'trial-vend-name',
    headerName: `Vendor: ${ctx.organizationName}`,
  },
  { field: 'group1', hide: true, rowGroup: true },
  { field: 'group2', hide: true, rowGroup: true },
  { field: 'group3', hide: true, rowGroup: true },
  { field: 'group4', hide: true, rowGroup: true },
  { field: 'display_label', headerName: 'Label', hide: true },
  { field: 'activity_name_label', headerName: 'Activities', hide: true },
  {
    field: 'changed',
    filter: true,
    headerName: 'Changed',
    hide: true,
  },
  {
    field: 'missing_activity',
    filter: true,
    headerName: 'Missing Activities',
    hide: true,
  },
];

const getAttributesColumnGroupDef = (
  ctx: CompareBudgetColumnContext
): ColGroupDef<CompareGridData> => {
  const attributes = getAttributeColumns({
    attributes: ctx.budgetData.map((z) => z.attributes || []),
  });

  return attributeColumnDef(attributes, ChangeOrderAttributesLSKey, ctx.budgetData, true);
};

const getAutoGroupColumnDef = (
  ctx: CompareBudgetColumnContext,
  attributesColumnGroupDef: ColGroupDef<CompareGridData>
): ColDef<CompareGridData> => {
  return {
    cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT,
    cellRenderer: AgBeCheckboxGroupRendererComponent,
    cellRendererParams: <ICheckboxCellRendererParams<CompareGridData>>{
      checkboxContainerClass: 'mr-[10px] pl-[6px] pr-[15px]',
      checkboxTooltip: (params) => {
        if (ctx.rowSelection.disabled) {
          return [ctx.rowSelection.disabledTooltip];
        }

        if (ctx.toBudgetVersion && params.data?.missing_activity) {
          return ['Activity cannot be selected, because it was deleted in this Change Order.'];
        }

        return [];
      },
      tooltipField: 'activity_name',
    },
    field: 'activity_name',
    headerClass: ['activities-header', '!pl-0'],
    headerComponent: AgBudgetGroupHeaderComponent,
    headerComponentParams: <AgBudgetAttributeComponentParams>{
      afterAttrToggle: ({ api }) => {
        api.sizeColumnsToFit();
      },
      columnsToCollapse: attributesColumnGroupDef.children.map((x: ColDef) => x.colId || x.field),
      expandLevel: () => -1,
      localStorageKey: ChangeOrderAttributesLSKey,
      showCheckbox: {
        disabled: ctx.rowSelection.disabled,
        tooltip: ctx.rowSelection.disabled ? ctx.rowSelection.disabledTooltip : '',
      },
      template: `Activities`,
    },
    headerName: 'Activities',
    minWidth: 300,
    pinned: 'left',
    resizable: true,
    width: 300,
  };
};

const getCurrentBudgetColumnGroupDef = (
  ctx: CompareBudgetColumnContext
): ColGroupDef<CompareGridData> => {
  return {
    children: [
      {
        field: 'from_activity_no',
        headerName: 'Activity ID',
        hide: true,
      },
      {
        ...unitColumn,
        field: 'from_unit',
        headerName: 'Unit(s)',
      },
      {
        ...unitCost(ctx),
        field: 'from_unit_cost',
        headerName: 'Unit Cost',
      },
      {
        ...unitCost(ctx, true),
        aggFunc: 'sum',
        field: 'from_total_cost',
        headerName: 'Total Cost',
      },
    ],
    headerClass: ['ag-header-align-center blue-header header-marker'],
    headerName: 'Current Budget',
  };
};

const getToBudgetColumnGroupDef = (
  ctx: CompareBudgetColumnContext
): ColGroupDef<CompareGridData> => {
  return {
    children: [
      {
        field: 'to_activity_no',
        headerName: 'Activity ID',
        hide: true,
      },
      {
        ...unitColumn,
        field: 'to_unit',
        headerName: 'Unit(s)',
      },
      {
        ...unitCost(ctx),
        field: 'to_unit_cost',
        headerName: 'Unit Cost',
      },
      {
        ...unitCost(ctx, true),
        aggFunc: 'sum',
        field: 'to_total_cost',
        headerName: 'Total Cost',
      },
    ],
    headerClass: ['ag-header-align-center green-header header-marker'],
    headerName: 'To Budget',
  };
};

const getVarianceColumnGroupDef = (
  ctx: CompareBudgetColumnContext
): ColGroupDef<CompareGridData> => {
  return {
    children: [
      {
        ...unitColumn,
        cellRenderer: AgPulseMinusComponent,
        field: 'variance_unit',
        headerName: 'Unit(s)',
      },
      {
        ...unitCost(ctx),
        cellRenderer: AgPulseMinusComponent,
        cellRendererParams: {
          org_currency: ctx.currency,
        },
        field: 'variance_unit_cost',
        headerName: 'Unit Cost',
      },
      {
        ...unitCost(ctx),
        aggFunc: 'sum',
        cellRenderer: AgPulseMinusComponent,
        cellRendererParams: {
          org_currency: ctx.currency,
        },
        field: 'variance_total_cost',
        headerName: `Total Cost`,
      },
      {
        aggFunc: (params) => {
          let var_total = 0;
          let from_total = 0;

          for (const childRow of params.rowNode.allLeafChildren) {
            var_total += childRow.data?.variance_total_cost || 0;
            from_total += childRow.data?.from_total_cost || 0;
          }

          return from_total ? (var_total || 0) / (from_total || 1) : 0;
        },
        cellClass: ['ag-cell-align-right', 'budget-percent', AuxExcelStyleKeys.BORDER_RIGHT],
        cellRenderer: AgPulseMinusComponent,
        field: 'variance_total_percent',
        headerClass: 'ag-header-align-center',
        headerName: 'Total (%)',
        minWidth: 80,
        resizable: true,
        width: 150,
      },
    ],
    headerClass: ['ag-header-align-center green-header header-marker'],
    headerName: 'Variance',
  };
};

const getExcelStyles = (): ExcelStyle[] => {
  return [
    ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS),
    ...AuxExcelStyles,
    {
      dataType: 'Number',
      id: 'budgetCostNoSymbol',
      numberFormat: { format: AuxExcelFormats.Units },
    },
    {
      dataType: 'Number',
      id: 'budget-cost',
      numberFormat: { format: AuxExcelFormats.Cost },
    },
    {
      dataType: 'Number',
      id: 'budget-percent',
      numberFormat: { format: AuxExcelFormats.Percent },
    },
  ];
};

// eslint-disable-next-line max-lines-per-function
export const exportChangeOrderBudget = ({
  fileName,
  gridAPI,
  org_currency,
  toBudgetVersion,
  trial,
}: {
  fileName: string;
  gridAPI: GridApi<CompareGridData>;
  org_currency: Currency;
  toBudgetVersion?: Maybe<CompareBudgetVersion>;
  trial: TrialModel;
}) => {
  // first column is the auto column ignore it
  const displayedColumns = gridAPI
    .getAllDisplayedColumns()
    .slice(1)
    .map((col) => col.getColId());
  const fromUnitIndex = displayedColumns.findIndex((col) => col === 'from_unit');
  const attributesColumns = displayedColumns.slice(0, fromUnitIndex);

  const columnKeys = [
    'cost_category',
    'group0',
    'display_label',
    'activity_name_label',
    ...attributesColumns,
    'from_activity_no',
    'from_unit',
    'from_unit_cost',
    'from_total_cost',
    ...(toBudgetVersion
      ? [
          'to_activity_no',
          'to_unit',
          'to_unit_cost',
          'to_total_cost',

          'variance_unit',
          'variance_unit_cost',
          'variance_total_cost',
          'variance_total_percent',
        ]
      : []),
  ];

  const { from_total_cost, to_total_cost, variance_total_cost, variance_total_percent } =
    gridAPI.getGridOption('pinnedBottomRowData')![0] as CompareGridData;

  const appendContent: ExcelRow[] = [
    {
      cells: [
        {
          data: { type: 'String', value: `Total` },
          mergeAcross: 3 + attributesColumns.length,
          styleId: 'total_row_header',
        },
      ],
    },
  ];

  const addToTotal = (value: number, merge: number) => {
    appendContent[0].cells.push({
      data: { type: 'Number', value: `${value}` },
      mergeAcross: merge,
      styleId: `total_row_${org_currency}`,
    });
  };

  addToTotal(from_total_cost, 3);

  if (toBudgetVersion) {
    addToTotal(to_total_cost, 3);
    addToTotal(variance_total_cost, 2);
    appendContent[0].cells.push({
      data: {
        type: 'Number',
        value: `${variance_total_percent}`,
      },
      styleId: 'total_row_percent',
    });
  }

  gridAPI.exportDataAsExcel({
    appendContent,
    author: 'Auxilius',
    columnKeys,
    columnWidth: (params) => {
      switch (params.column?.getId()) {
        case 'activity_name_label':
          return 490;
        case 'group0':
          return 280;
        default:
          return 105;
      }
    },
    fileName,
    fontSize: 11,
    prependContent: [
      {
        cells: [
          {
            data: { type: 'String', value: `Trial: ${trial.name}` },
            mergeAcross: 1,
            styleId: 'first_row',
          },
        ],
      },
    ],
    sheetName: 'Compare Budgets',
    shouldRowBeSkipped: (params) => !params.node?.data?.cost_category,
  });
};
